/**
 * Shot Location & Zone Mapping — shared geometry/classification (frontend).
 *
 * Coordinates are normalized to the "attacking end" diagram used for both
 * Us shots (on the opponent's goal) and Them shots (on our goal):
 *   x: 0 -> 1 across 44 yards (the 40yd attack area + 2yd margin each side)
 *   y: 0 (TOP, 4yd behind the goal line) -> 1 (BOTTOM, 4yd past the
 *      restraining line) — 28 yards, with the goal line at y=0.1429 and the
 *      restraining line at y=0.8571
 *
 * Both axes are true scale, so distances derived from x/y are real yards.
 *
 * NOTE: Keep this logic in sync with the backend copy at
 * StatdogBackend/helpers/shotLocation.js. The server always recomputes
 * zone/hash from x/y on write, so this copy only needs to match closely
 * enough that the live picker's preview (zone label, inside/outside hash)
 * matches what gets stored.
 */

export type ShotOutcome = 'goal' | 'saved' | 'missed' | 'blocked';

export type ShotZone =
  | 'crease'
  | 'slot'
  | 'left_alley'
  | 'right_alley'
  | 'left_wing'
  | 'high_slot'
  | 'right_wing'
  | 'high'
  | 'left_high'
  | 'right_high';

export type ShotHash = 'inside' | 'outside';

export interface ShotLocation {
  x: number;
  y: number;
  zone: ShotZone;
  hash: ShotHash;
  source?: 'live' | 'postgame';
}

export interface ShotChartRow {
  _id: string;
  type: string;
  outcome: ShotOutcome | null;
  player: 'us' | 'them';
  playerId?: string | number;
  playerName?: string;
  playerNumber?: number | string | null;
  /** season_players._id — stable across games, unlike playerId (game_players._id). */
  playerSeasonId?: string | null;
  /** Our goals only: true when an Assist was logged for the goal. */
  assisted?: boolean;
  period: string;
  time: string;
  gameId?: string;
  shotLocation: ShotLocation | null;
}

export interface ShotChartSummary {
  totalShots: number;
  locatedShots: number;
  unlocatedShots: number;
  byOutcome: Record<ShotOutcome, number>;
  insideHash: { shots: number; goals: number };
  outsideHash: { shots: number; goals: number };
  zones: Record<ShotZone, { shots: number; goals: number }>;
}

export const SHOT_TYPE_TO_OUTCOME: Record<string, ShotOutcome> = {
  'Shot Goal': 'goal',
  'Shot Saved': 'saved',
  'Shot Missed': 'missed',
  'Shot Blocked': 'blocked',
};

export const LOCATABLE_SHOT_TYPES = Object.keys(SHOT_TYPE_TO_OUTCOME);

export const ZONE_LABELS: Record<ShotZone, string> = {
  crease: 'Crease',
  slot: 'Slot',
  left_alley: 'Left Alley',
  right_alley: 'Right Alley',
  left_wing: 'Left Wing',
  high_slot: 'High Slot',
  right_wing: 'Right Wing',
  high: 'Top Center',
  left_high: 'Left Top',
  right_high: 'Right Top',
};

/**
 * Real NCAA men's field dimensions for the attacking end (yards).
 * Source: NCAA Men's Lacrosse "Field of Play" diagram — 110x60 field, goal
 * 15yd off the end line, 9ft-radius crease, 40yd-wide attack area, restraining
 * line 20yd from the goal line.
 *
 * Shots never originate from behind the goal, so the drawing starts just
 * behind the goal line (enough to show the cage and back of the crease) and
 * runs a few yards past the restraining line for long-range shots.
 */
export const FIELD_YARDS = {
  /**
   * The full field is 60yd wide, but the drawing is cropped to the 40yd-wide
   * attack area (a real NCAA marking) plus a 2yd out-of-bounds margin on each
   * side. Cropping keeps the crease and slot big enough to tap accurately
   * instead of wasting a third of the picture on space nobody shoots from.
   */
  fullFieldWidth: 60,
  width: 44, // drawn width: 40yd attack area + 2yd margin per side
  attackAreaInset: 2, // where the attack-area line sits in from each edge
  behindGoalLine: 4, // drawn area behind the goal line (cage + back of crease only)
  goalLineToRestraining: 20,
  pastRestraining: 4, // drawn area past the restraining line

  creaseRadius: 3, // 9 ft
  goalMouthWidth: 2, // 6 ft
  goalNetDepth: 7 / 3, // 7 ft, net tapers back toward the end line
  // Coaching reference lane (not a painted line): "inside the hashes" is the
  // 12yd-wide prime shooting lane in front of the cage.
  hashHalfWidth: 6,
  slotDepth: 8, // prime scoring area in front of the goal
  wingDepth: 14, // alley/wing break point
};

const DEPTH_YARDS = FIELD_YARDS.behindGoalLine + FIELD_YARDS.goalLineToRestraining + FIELD_YARDS.pastRestraining; // 28

// Normalized (0-1) geometry derived from the real yardages. Drawing and
// classification both read from here so they can never drift apart.
export const FIELD_GEOMETRY = {
  widthYards: FIELD_YARDS.width,
  depthYards: DEPTH_YARDS,
  goalCenterX: 0.5,
  goalLineY: FIELD_YARDS.behindGoalLine / DEPTH_YARDS,
  restrainingY: (FIELD_YARDS.behindGoalLine + FIELD_YARDS.goalLineToRestraining) / DEPTH_YARDS,
  creaseRadiusX: FIELD_YARDS.creaseRadius / FIELD_YARDS.width,
  creaseRadiusY: FIELD_YARDS.creaseRadius / DEPTH_YARDS,
  goalMouthHalfX: FIELD_YARDS.goalMouthWidth / 2 / FIELD_YARDS.width,
  goalNetDepthY: FIELD_YARDS.goalNetDepth / DEPTH_YARDS,
  attackAreaLeftX: FIELD_YARDS.attackAreaInset / FIELD_YARDS.width,
  attackAreaRightX: (FIELD_YARDS.width - FIELD_YARDS.attackAreaInset) / FIELD_YARDS.width,
  hashLeftX: (FIELD_YARDS.width / 2 - FIELD_YARDS.hashHalfWidth) / FIELD_YARDS.width,
  hashRightX: (FIELD_YARDS.width / 2 + FIELD_YARDS.hashHalfWidth) / FIELD_YARDS.width,
  slotEndY: (FIELD_YARDS.behindGoalLine + FIELD_YARDS.slotDepth) / DEPTH_YARDS,
  wingEndY: (FIELD_YARDS.behindGoalLine + FIELD_YARDS.wingDepth) / DEPTH_YARDS,
};

const clamp01 = (n: number) => Math.max(0, Math.min(1, n));

/** Clamp a raw tap onto the field, never behind goal line extended (GLE). */
export function clampShotPoint(x: number, y: number): { x: number; y: number } {
  return { x: clamp01(x), y: Math.max(FIELD_GEOMETRY.goalLineY, clamp01(y)) };
}

/** Straight-line distance from the middle of the goal line, in yards. */
export function shotDistanceYards(x: number, y: number): number {
  const p = clampShotPoint(x, y);
  const dx = (p.x - FIELD_GEOMETRY.goalCenterX) * FIELD_GEOMETRY.widthYards;
  const dy = (p.y - FIELD_GEOMETRY.goalLineY) * FIELD_GEOMETRY.depthYards;
  return Math.sqrt(dx * dx + dy * dy);
}

/**
 * Classify a normalized (x, y) tap into a coaching zone + hash side.
 *
 * Zones are a 3x3 grid in front of the cage plus the crease:
 *   GLE -> 8yd:          Left Alley | Slot       | Right Alley
 *   8yd -> 14yd:         Left Wing  | High Slot  | Right Wing
 *   14yd -> past line:   Left Top   | Top Center | Right Top
 * The middle column is the 12yd "inside the hashes" lane.
 */
export function classifyShotLocation(rawX: number, rawY: number): { zone: ShotZone; hash: ShotHash } {
  const { x, y } = clampShotPoint(rawX, rawY);
  const g = FIELD_GEOMETRY;

  const hash: ShotHash = x >= g.hashLeftX && x <= g.hashRightX ? 'inside' : 'outside';
  const isLeft = x < g.hashLeftX;

  // Crease is measured in yards so it stays a true circle.
  if (shotDistanceYards(x, y) <= FIELD_YARDS.creaseRadius) {
    return { zone: 'crease', hash: 'inside' };
  }

  if (y <= g.slotEndY) {
    if (hash === 'inside') return { zone: 'slot', hash };
    return { zone: isLeft ? 'left_alley' : 'right_alley', hash };
  }

  if (y <= g.wingEndY) {
    if (hash === 'inside') return { zone: 'high_slot', hash };
    return { zone: isLeft ? 'left_wing' : 'right_wing', hash };
  }

  if (hash === 'inside') return { zone: 'high', hash };
  return { zone: isLeft ? 'left_high' : 'right_high', hash };
}

export function buildShotLocation(x: number, y: number, source: 'live' | 'postgame' = 'live'): ShotLocation {
  const p = clampShotPoint(x, y);
  const { zone, hash } = classifyShotLocation(p.x, p.y);
  return { x: p.x, y: p.y, zone, hash, source };
}

// Outcome visual encoding — shared by the picker preview and the chart legend.
export const OUTCOME_STYLE: Record<
  ShotOutcome,
  { label: string; color: string; shape: 'circle' | 'diamond' | 'x' | 'square' }
> = {
  goal: { label: 'Goal', color: '#7fe526', shape: 'circle' },
  missed: { label: 'Missed', color: '#e6b800', shape: 'x' },
  saved: { label: 'Saved', color: '#294e87', shape: 'diamond' },
  blocked: { label: 'Blocked', color: '#9b3a2f', shape: 'square' },
};

/** Assisted goals stay in the green family, distinct from the bright unassisted circle. */
export const ASSISTED_GOAL_COLOR = '#2ec4b6';

export function emptyShotSummary(): ShotChartSummary {
  const zones = Object.keys(ZONE_LABELS).reduce(
    (acc, zone) => ({ ...acc, [zone]: { shots: 0, goals: 0 } }),
    {} as Record<ShotZone, { shots: number; goals: number }>,
  );
  return {
    totalShots: 0,
    locatedShots: 0,
    unlocatedShots: 0,
    byOutcome: { goal: 0, saved: 0, missed: 0, blocked: 0 },
    insideHash: { shots: 0, goals: 0 },
    outsideHash: { shots: 0, goals: 0 },
    zones,
  };
}

/** Clock time of a stat log, matching the stats list (24-hour h:mm:ss). */
export function formatShotTime(time?: string | null): string {
  if (!time) return '';
  const date = new Date(time);
  if (Number.isNaN(date.getTime())) return String(time);
  return date.toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  });
}

export function pct(numerator: number, denominator: number): string {
  if (!denominator) return '—';
  return `${Math.round((numerator / denominator) * 100)}%`;
}

/** Recompute a ShotChartSummary from a (possibly client-filtered) row list. */
export function summarizeRows(rows: ShotChartRow[]): ShotChartSummary {
  const summary = emptyShotSummary();
  summary.totalShots = rows.length;

  rows.forEach((row) => {
    if (row.outcome && summary.byOutcome[row.outcome] !== undefined) {
      summary.byOutcome[row.outcome] += 1;
    }
    if (!row.shotLocation) return;

    summary.locatedShots += 1;
    const isGoal = row.outcome === 'goal';
    // Reclassify from x/y so the chart always matches the field as drawn now.
    const { zone, hash } = classifyShotLocation(row.shotLocation.x, row.shotLocation.y);
    const hashBucket = hash === 'inside' ? summary.insideHash : summary.outsideHash;
    hashBucket.shots += 1;
    if (isGoal) hashBucket.goals += 1;

    const zoneBucket = summary.zones[zone];
    if (zoneBucket) {
      zoneBucket.shots += 1;
      if (isGoal) zoneBucket.goals += 1;
    }
  });

  summary.unlocatedShots = summary.totalShots - summary.locatedShots;
  return summary;
}
